import { useState, useEffect } from 'react';
import {
  Button,
  Group,
  Modal,
  Stack,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { WeclappContactDetail, WeclappContactInput } from '../lib/api/domains/weclapp';
import type { CWContactDetail, CWContactInput } from '../lib/api/domains/cwLookups';

interface WeclappMode {
  system: 'weclapp';
  partyId: string;
  existingContact?: WeclappContactDetail;
  /** Neuer Kontakt: Name vorbelegen („Max Wagner" → Vorname Max, Nachname Wagner) */
  prefillName?: string;
}

interface CWMode {
  system: 'cw';
  companyId: number;
  existingContact?: CWContactDetail;
}

interface ContactModalProps {
  opened: boolean;
  onClose: () => void;
  mode: WeclappMode | CWMode;
  /** Wird nach erfolgreichem Speichern mit dem neuen/geänderten Kontakt aufgerufen */
  onSaved: (contact: { id: string; name: string; email?: string }) => void;
  /** Aus einem anderen Dialog heraus geöffnet (KIAN-Entwurf, zIndex 5300) — sonst läge es dahinter. */
  zIndex?: number;
}

/** „Max Wagner" → Max / Wagner; ein einzelnes Wort ist der Nachname. */
export function splitPrefillName(name: string | undefined): { firstName: string; lastName: string } {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: '', lastName: parts[0] };
  return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1] };
}

export function ContactModal({ opened, onClose, mode, onSaved, zIndex }: ContactModalProps) {
  const existing = mode.existingContact;
  const isEdit = !!existing;
  const prefill = splitPrefillName(mode.system === 'weclapp' && !existing ? mode.prefillName : undefined);

  const [firstName, setFirstName] = useState(existing?.firstName ?? prefill.firstName);
  const [lastName, setLastName] = useState(existing?.lastName ?? prefill.lastName);
  const [email, setEmail] = useState(existing?.email ?? '');
  const [phone, setPhone] = useState(
    mode.system === 'weclapp'
      ? ((existing as WeclappContactDetail | undefined)?.phone ?? '')
      : ((existing as CWContactDetail | undefined)?.phone ?? '')
  );
  const [mobilePhone, setMobilePhone] = useState(
    mode.system === 'weclapp'
      ? ((existing as WeclappContactDetail | undefined)?.mobilePhone ?? '')
      : ''
  );

  // State aktualisieren wenn sich der zu bearbeitende Kontakt ändert (z.B. Modal für anderen AP geöffnet)
  const modeSystem = mode.system;
  useEffect(() => {
    setFirstName(existing?.firstName ?? prefill.firstName);
    setLastName(existing?.lastName ?? prefill.lastName);
    setEmail(existing?.email ?? '');
    setPhone(
      modeSystem === 'weclapp'
        ? ((existing as WeclappContactDetail | undefined)?.phone ?? '')
        : ((existing as CWContactDetail | undefined)?.phone ?? '')
    );
    setMobilePhone(
      modeSystem === 'weclapp'
        ? ((existing as WeclappContactDetail | undefined)?.mobilePhone ?? '')
        : ''
    );
  }, [existing, modeSystem]);

  const queryClient = useQueryClient();

  const weclappCreate = useMutation({
    mutationFn: (input: WeclappContactInput) =>
      api.createWeclappContact((mode as WeclappMode).partyId, input),
    onSuccess: ({ contact }) => {
      queryClient.invalidateQueries({ queryKey: ['weclapp-contacts', (mode as WeclappMode).partyId] });
      notifications.show({ color: 'green', message: 'Ansprechpartner angelegt.' });
      onSaved({ id: contact.id, name: contact.name, email: contact.email });
      onClose();
    },
    onError: (err: Error) => {
      notifications.show({ color: 'red', title: 'Fehler', message: err.message });
    },
  });

  const weclappUpdate = useMutation({
    mutationFn: (input: WeclappContactInput) => {
      const wMode = mode as WeclappMode;
      if (!wMode.existingContact?.id) throw new Error('Kein bestehender Kontakt zum Aktualisieren');
      return api.updateWeclappContact(wMode.partyId, wMode.existingContact.id, input);
    },
    onSuccess: ({ contact }) => {
      queryClient.invalidateQueries({ queryKey: ['weclapp-contacts', (mode as WeclappMode).partyId] });
      notifications.show({ color: 'green', message: 'Ansprechpartner aktualisiert.' });
      onSaved({ id: contact.id, name: contact.name, email: contact.email });
      onClose();
    },
    onError: (err: Error) => {
      notifications.show({ color: 'red', title: 'Fehler', message: err.message });
    },
  });

  const cwCreate = useMutation({
    mutationFn: (input: CWContactInput) =>
      api.createCwContact((mode as CWMode).companyId, input),
    onSuccess: ({ contact }) => {
      queryClient.invalidateQueries({ queryKey: ['cwContacts', (mode as CWMode).companyId] });
      notifications.show({ color: 'green', message: 'Ansprechpartner angelegt.' });
      onSaved({
        id: String(contact.id),
        name: `${contact.firstName} ${contact.lastName}`.trim(),
        email: contact.email,
      });
      onClose();
    },
    onError: (err: Error) => {
      notifications.show({ color: 'red', title: 'Fehler', message: err.message });
    },
  });

  const cwUpdate = useMutation({
    mutationFn: (input: CWContactInput) => {
      const cMode = mode as CWMode;
      if (!cMode.existingContact?.id) throw new Error('Kein bestehender Kontakt zum Aktualisieren');
      return api.updateCwContact(cMode.existingContact.id, input);
    },
    onSuccess: ({ contact }) => {
      queryClient.invalidateQueries({ queryKey: ['cwContacts', (mode as CWMode).companyId] });
      notifications.show({ color: 'green', message: 'Ansprechpartner aktualisiert.' });
      onSaved({
        id: String(contact.id),
        name: `${contact.firstName} ${contact.lastName}`.trim(),
        email: contact.email,
      });
      onClose();
    },
    onError: (err: Error) => {
      notifications.show({ color: 'red', title: 'Fehler', message: err.message });
    },
  });

  const isPending =
    weclappCreate.isPending || weclappUpdate.isPending || cwCreate.isPending || cwUpdate.isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) return;

    const input = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      mobilePhone: mobilePhone.trim() || undefined,
    };

    if (mode.system === 'weclapp') {
      if (isEdit) weclappUpdate.mutate(input);
      else weclappCreate.mutate(input);
    } else {
      if (isEdit) cwUpdate.mutate(input);
      else cwCreate.mutate(input);
    }
  }

  const title = isEdit ? 'Ansprechpartner bearbeiten' : 'Neuen Ansprechpartner anlegen';

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="sm" zIndex={zIndex}>
      <form onSubmit={handleSubmit}>
        <Stack gap="sm">
          <Group grow>
            <TextInput
              label="Vorname"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.currentTarget.value)}
              disabled={isPending}
            />
            <TextInput
              label="Nachname"
              required
              value={lastName}
              onChange={(e) => setLastName(e.currentTarget.value)}
              disabled={isPending}
            />
          </Group>
          <TextInput
            label="E-Mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            disabled={isPending}
          />
          <TextInput
            label="Telefon"
            value={phone}
            onChange={(e) => setPhone(e.currentTarget.value)}
            disabled={isPending}
          />
          {mode.system === 'weclapp' && (
            <TextInput
              label="Mobil"
              value={mobilePhone}
              onChange={(e) => setMobilePhone(e.currentTarget.value)}
              disabled={isPending}
            />
          )}
          <Group justify="flex-end" mt="xs">
            <Button variant="subtle" onClick={onClose} disabled={isPending}>
              Abbrechen
            </Button>
            <Button type="submit" loading={isPending}>
              {isEdit ? 'Speichern' : 'Anlegen'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
